import {
    AnalyticsMethod,
    AnalyticsFlowEvent,
    AnalyticsAuthenticationEvent,
    AnalyticsEvent,
    AnalyticsEventData
} from "../types";

/**
 * Validator function for analytics adapters.
 */
export type Validator = (
    this: AnalyticsAbstractAdapter,
    method: AnalyticsMethod,
    id?: string,
    data?: Record<string, any>
) => void;

export abstract class AnalyticsAbstractAdapter {
    /** one instance per concrete subclass                          */
    private static instances: Map<new () => AnalyticsAbstractAdapter, AnalyticsAbstractAdapter> =
        new Map();

    /** list of allowed methods / event IDs                         */
    protected readonly methods: AnalyticsMethod[] = Object.values(AnalyticsMethod);
    protected readonly registry: AnalyticsEvent[] = [
        ...Object.values(AnalyticsFlowEvent),
        ...Object.values(AnalyticsAuthenticationEvent)
    ];
    protected readonly enabledMethods: AnalyticsMethod[] = [];

    protected methodValidator(
        this: AnalyticsAbstractAdapter,
        method: AnalyticsMethod,
        _id?: string,
        _data?: Record<string, any>
    ) {
        if (!this.methods.includes(method)) {
            throw new Error(`Method ${method} is not registered`);
        }
    }

    private idValidator(
        this: AnalyticsAbstractAdapter,
        method: AnalyticsMethod,
        id?: string,
        _data?: Record<string, any>
    ) {
        if (method === AnalyticsMethod.TRACK && !this.registry.includes(id as AnalyticsEvent)) {
            throw new Error(`ID ${id} is not registered`);
        }
    }

    protected validators: Validator[] = [this.methodValidator, this.idValidator];

    /** subclasses get their own copy of this static method         */
    static getInstance<T extends AnalyticsAbstractAdapter>(this: new () => T): T {
        let instance = AnalyticsAbstractAdapter.instances.get(this) as T | undefined;
        if (!instance) {
            instance = new this();
            AnalyticsAbstractAdapter.instances.set(this, instance);
        }
        return instance;
    }

    /** public façade used by hooks / codebase                      */
    onEvent({
        method,
        id,
        data
    }: {
        method: AnalyticsMethod;
        data: AnalyticsEventData;
        id?: string;
    }) {
        try {
            this.validators.forEach((validator) => validator.call(this, method, id, data));

            if (!this.enabledMethods.includes(method)) {
                return;
            }

            return this.postEvent({ method, id, data });
        } catch (err) {
            console.error(
                `[Analytics] ${this.adapterName} error:`,
                err,
                method,
                JSON.stringify({ id, data }, null, 2)
            );
        }
    }

    /** subclasses must implement how the data is actually sent     */
    protected abstract postEvent(args: {
        method: AnalyticsMethod;
        data: AnalyticsEventData;
        id?: string;
    }): unknown;

    /** optional: nicer log output                                  */
    protected get adapterName(): string {
        return this.constructor.name;
    }
}
